import { forwardRef, useCallback } from 'react';
import type { MouseEvent } from 'react';

import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import ButtonBase from '@mui/material/ButtonBase';
import { useRouter } from 'next/navigation';

import { useNavItem } from '../hooks';
import { pathForNextNavigation } from '@/lib/appBasePath';
import { Iconify } from '../../iconify';
import { iconifyClasses } from '../../iconify/classes';
import { navSectionClasses } from '../classes';
import { stateClasses, sharedStyles } from '../styles';

import type { NavItemProps, NavItemStateProps } from '../types';

// ----------------------------------------------------------------------

export const NavItem = forwardRef<HTMLButtonElement, NavItemProps>(
  (
    {
      path,
      icon,
      info,
      title,
      caption,
      //
      open,
      depth,
      render,
      active,
      disabled,
      hasChild,
      slotProps,
      externalLink,
      enabledRootRedirect,
      onToggleMenu,
      onClick: onClickProp,
      ...other
    },
    ref
  ) => {
    const router = useRouter();
    const navItem = useNavItem({
      path,
      icon,
      info,
      depth,
      render,
      hasChild,
      externalLink,
      enabledRootRedirect,
    });

    // Prefetch route on hover for instant navigation
    const handleMouseEnter = useCallback(() => {
      if (path && !externalLink && !hasChild) {
        router.prefetch(pathForNextNavigation(path));
      }
    }, [path, externalLink, hasChild, router]);

    const handleItemClick = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        onClickProp?.(event);
        if (event.defaultPrevented) return;
        if (hasChild && !enabledRootRedirect) {
          event.preventDefault();
          onToggleMenu?.();
        }
      },
      [onClickProp, hasChild, enabledRootRedirect, onToggleMenu]
    );

    return (
      <StyledNavItem
        ref={ref}
        aria-label={title}
        depth={depth}
        active={active}
        disabled={disabled}
        hasChild={hasChild}
        open={open && !active}
        onMouseEnter={handleMouseEnter}
        sx={{
          ...slotProps?.sx,
          display: 'flex',
          alignItems: 'center',
          gap: '9px',
          [`& .${navSectionClasses.item.icon}`]: slotProps?.icon,
          [`& .${navSectionClasses.item.texts}`]: slotProps?.texts,
          [`& .${navSectionClasses.item.title}`]: slotProps?.title,
          [`& .${navSectionClasses.item.caption}`]: slotProps?.caption,
          [`& .${navSectionClasses.item.info}`]: slotProps?.info,
          [`& .${navSectionClasses.item.arrow}`]: slotProps?.arrow,
        }}
        className={stateClasses({ open: open && !active, active, disabled })}
        {...navItem.baseProps}
        {...other}
        onClick={handleItemClick}
      >
        {/* Icon — root .nitem only; HTML .nsub rows are text */}
        {icon && depth === 1 && (
          <Box
            component="span"
            className={navSectionClasses.item.icon}
            sx={{
              width: 14,
              height: 14,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              color: 'inherit',
              [`& svg, & img`]: { width: 14, height: 14 },
            }}
          >
            {navItem.renderIcon}
          </Box>
        )}

        {/* Text and caption */}
        <Box component="span" className={navSectionClasses.item.texts} sx={{ flex: 1, display: 'flex', alignItems: 'center', minWidth: 0 }}>
          {title && (
            <Box
              component="span"
              className={navSectionClasses.item.title}
              sx={{ 
                whiteSpace: 'nowrap', 
                overflow: 'hidden', 
                textOverflow: 'ellipsis' 
              }}
            >
              {title}
            </Box>
          )}
          {caption && (
            <Tooltip title={caption} placement="top-start">
              <Box component="span" className={navSectionClasses.item.caption}>
                {caption}
              </Box>
            </Tooltip>
          )}
        </Box>

        {/* Info — Command Center ntag: Live (success) / Beta (purple) */}
        {info && (
          <Box
            component="span"
            className={navSectionClasses.item.info}
            sx={{
              ml: 'auto',
              flexShrink: 0,
            fontFamily: 'var(--font-body)',
            fontSize: 9,
            fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              lineHeight: 1.4,
              px: '7px',
              py: '2px',
              borderRadius: 'var(--r-full)',
              ...(typeof info === 'string' && info.toLowerCase() === 'live'
                ? { bgcolor: 'var(--success-bg)', color: 'var(--success-text)' }
                : typeof info === 'string' && info.toLowerCase() === 'beta'
                  ? { bgcolor: 'var(--yellow-bg)', color: 'var(--yellow-text)' }
                  : typeof info === 'string' && info.toLowerCase() === 'soon'
                    ? { bgcolor: 'var(--bg-hover)', color: 'var(--text-primary)' }
                    : { bgcolor: 'var(--bg-hover)', color: 'var(--text-primary)' }),
            }}
          >
            {typeof info === 'string' ? info : navItem.renderInfo}
          </Box>
        )}

        {/* Collapse chevron — required so Voice / Channels can close */}
        {hasChild && (
          <Iconify
            icon={open ? 'eva:arrow-ios-downward-fill' : 'eva:arrow-ios-forward-fill'}
            className={navSectionClasses.item.arrow}
            width={12}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleMenu?.();
            }}
            sx={{
              flexShrink: 0,
              opacity: 0.6,
              color: 'inherit',
              cursor: 'pointer',
            }}
          />
        )}
      </StyledNavItem>
    );
  }
);

NavItem.displayName = 'NavItem';

// ----------------------------------------------------------------------

const NAV_EASE = 'var(--t-fast) var(--ease)';

const StyledNavItem = styled(ButtonBase, {
  shouldForwardProp: (prop) =>
    prop !== 'active' && prop !== 'open' && prop !== 'disabled' && prop !== 'depth' && prop !== 'hasChild',
})<NavItemStateProps>(({ active, open, disabled, depth = 1, hasChild }) => {
  const rootItem = depth === 1;
  const subItem = !rootItem;
  const leafActive = Boolean(active && !hasChild);

  const iconBox = {
    ...sharedStyles.icon,
    width: 14,
    height: 14,
    margin: 0,
    color: 'inherit',
    opacity: 0.8,
    [`& .${iconifyClasses.root}`]: { width: '14px !important', height: '14px !important' },
    [`& .${iconifyClasses.root} svg`]: { width: '14px !important', height: '14px !important' },
  };

  const titleBase = {
    ...sharedStyles.noWrap,
    fontFamily: 'var(--font-body)',
    fontSize: 'inherit',
    fontWeight: 'inherit',
    lineHeight: 'inherit',
    letterSpacing: 'inherit',
    color: 'inherit',
  };

  return {
    width: '100%',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    borderRadius: 'var(--r-sm)',
    fontFamily: 'var(--font-body)',
    fontSize: 'var(--text-sm)',
    fontWeight: 500,
    lineHeight: 1.5,
    letterSpacing: 0,
    textTransform: 'none',
    color: 'var(--text-primary)',
    transition: `all ${NAV_EASE}`,
    '&:hover': {
      backgroundColor: 'var(--bg-hover)',
      color: 'var(--text-primary)',
    },
    [`& .${navSectionClasses.item.icon}`]: iconBox,
    [`& .${navSectionClasses.item.texts}`]: { minWidth: 0, flex: '1 1 auto' },
    [`& .${navSectionClasses.item.title}`]: titleBase,
    [`& .${navSectionClasses.item.caption}`]: {
      ...sharedStyles.noWrap,
      fontFamily: 'var(--font-body)',
      fontSize: 'var(--text-caption)',
      fontWeight: 400,
      color: 'var(--text-primary)',
    },
    [`& .${navSectionClasses.item.info}`]: { ...sharedStyles.info, marginLeft: 'auto' },

    /**
     * Root item — matches CommandCenterNavRail's row styles (13px / 500, 600 when active)
     */
    ...(rootItem && {
      paddingTop: '4px',
      paddingBottom: '4px',
      paddingRight: '5px',
      paddingLeft: `${6 + (depth - 1) * 10}px`,
      minHeight: '28px',
      borderRadius: 'var(--r-md)',
      color: 'var(--text-primary)',
      ...(open && {
        color: 'var(--text-primary)',
        backgroundColor: 'transparent',
        '&:hover': {
          backgroundColor: 'var(--bg-hover)',
          color: 'var(--text-primary)',
        },
      }),
      ...(leafActive && {
        color: 'var(--text-primary)',
        backgroundColor: 'var(--bg-active)',
        fontWeight: 600,
        '&:hover': {
          backgroundColor: 'var(--bg-active)',
          color: 'var(--text-primary)',
        },
      }),
    }),

    /**
     * Sub item — matches CommandCenterNavRail's row styles (12px / 500, 600 when active).
     * Left padding grows with depth (6 + (depth-1)*10, tightened from CC's own
     * 10 + depth*14 per user feedback that the indent felt too wide) instead of a fixed 10px.
     */
    ...(subItem && {
      paddingTop: '4px',
      paddingBottom: '4px',
      paddingRight: '5px',
      paddingLeft: `${6 + (depth - 1) * 10}px`,
      minHeight: '28px',
      fontSize: '12px',
      fontWeight: 500,
      borderRadius: 'var(--r-md)',
      color: 'var(--text-primary)',
      '&::before': { display: 'none' },
      ...(leafActive && {
        color: 'var(--text-primary)',
        backgroundColor: 'var(--bg-active)',
        fontWeight: 600,
        '&:hover': {
          backgroundColor: 'var(--bg-active)',
          color: 'var(--text-primary)',
        },
      }),
      ...(open && !leafActive && {
        color: 'var(--text-primary)',
        backgroundColor: 'transparent',
      }),
    }),

    ...(disabled && {
      opacity: 0.35,
      pointerEvents: 'none',
      cursor: 'default',
    }),
  };
});
      